import { prodaNameUtils } from "../hooks/useProdaName";
import { deepCopy } from "./common";
import {
  applyStateBasedOnAgeQuantity,
  parseFreeForAges,
  PRODA_CALENDAR_CONF,
  ProdAttrUtils,
} from "./productAttributeUtils";

export const modBookingState = (
  state: BookingState,
  payload: BookingAttributeDTO,
) => {
  const { productAttribute, value } = payload;
  const index = state.bookingAttributes.findIndex(
    (v) => v.productAttribute.id === productAttribute.id,
  );
  if (index !== -1) {
    state.bookingAttributes[index].value = {
      ...value,
      dpName:
        prodaNameUtils(productAttribute).join(" ") || productAttribute.name,
      paType: productAttribute.type,
      paName: productAttribute.name,
    };
  } else {
    state.bookingAttributes.push({ productAttribute, value });
  }
  // *BOOKMARK*: Pass quantity of children
  // nếu là pass thì cập nhật số lượng cho từng loại product
  if (ProdAttrUtils.PASS.isDefaultPa(productAttribute)) {
    const ageQuantities = value?.ageQuantities ?? [];
    state.childrenLs = state.childrenLs?.map((childState) => {
      childState.bookingAttributes = ProdAttrUtils.PASS.applyQuantityOfChildren(
        childState.bookingAttributes,
        ageQuantities,
      );
      return childState;
    });
  }
  const productType = state.product?.productType;
  if (
    productType === "PASS" &&
    (productAttribute.name === "CHILD" || productAttribute.name === "ADULT")
  ) {
    // nếu có premium plus thì thay đổi số lượng premium plus theo só lượng người
    const premiumPlus = ProdAttrUtils.findOptionalByName(state, "PREMIUM_PLUS");
    if (premiumPlus) {
      //  && premiumPlus.value?.quantity
      // PREMIUM PLUS là bắt buộc khi mua PASS
      const count = ProdAttrUtils.countPerson(state);
      premiumPlus.value = BookVal.overwrite(premiumPlus)({
        dpName: prodaNameUtils(premiumPlus.productAttribute).join(" "),
        quantity: count,
      }).value;
    }

    (["SIM", "Q_SIM"] as ProdAttrName[]).forEach((name) => {
      const qSim = ProdAttrUtils.findOptionalByName(state, name);
      if (qSim) {
        if (name === "SIM" && typeof qSim.value?.quantity === "undefined") {
          return;
        }

        // Q_SIM tính theo người lớn
        const count = ProdAttrUtils.countAdult(state);
        qSim.value = BookVal.overwrite(qSim)({
          dpName: prodaNameUtils(qSim.productAttribute).join(" "),
          quantity: count,
        }).value;
      }
    });
  }
  if (productType === "MUSICAL" && productAttribute.name === "PERSON") {
    // *BOOKMARK*: cập nhật số lượng người cho MUSICAL
    const bean = ProdAttrUtils.findOptionalByName(state, "MUSICAL_3RDPARTY");
    if (bean && bean.value) {
      const count = ProdAttrUtils.countPerson(state);
      bean.value = BookVal.overwrite(bean)({
        quantity: count,
        extProductBookingValues: bean.value.extProductBookingValues?.map(
          (x) => {
            return {
              ...x,
              quantity: count,
            };
          },
        ),
      }).value;
    }
  }

  return state;
};

export const product2BookingAttributes = ({
  product,
  oldState,
}: {
  product?: ProductDetailResponse;
  oldState?: BookingState | null;
}): BookingAttributeDTO[] => {
  return (
    product?.attributeList?.map((productAttribute) => {
      const booka = oldState?.bookingAttributes?.find(
        (ba) => ba.productAttribute.id === productAttribute.id,
      );
      if (booka?.value) {
        return booka as BookingAttributeDTO;
      }
      return {
        productAttribute,
        value: booka?.value,
      } as BookingAttributeDTO;
    }) ?? []
  );
};
const appendAgeQuantityLs =
  (defaultBooka?: BookingAttributeDTO | null) => (dto: AgeQuantityDTO) => {
    return defaultBooka
      ? {
          ...defaultBooka,
          value: {
            ...defaultBooka.value,
            ageQuantities: ProdAttrUtils.CHILD.attachToAgeQuantityLs({
              ls: defaultBooka.value?.ageQuantities ?? [],
              age: dto.age,
              quantity: dto.quantity,
            }),
          },
        }
      : null;
  };

const appendBookaValue =
  (booka?: BookingAttributeDTO | null) => (value: BookingAttributeValueDTO) => {
    const tmp = deepCopy(booka);
    if (tmp) {
      if (!tmp.value) tmp.value = {};
      const keys = Object.keys(value) as BookingAttributeValueDTOKeys[];
      for (const k of keys) {
        // @ts-ignore
        tmp.value[k] = value[k];
      }
    }
    return tmp;
  };
const overwriteBookaValue =
  (booka?: BookingAttributeDTO | null) => (value: BookingAttributeValueDTO) => {
    return {
      ...booka,
      value,
    } as BookingAttributeDTO;
  };

export const BookVal = {
  age: appendAgeQuantityLs,
  append: appendBookaValue,
  overwrite: overwriteBookaValue,
};

export const extractInvalidAgeProductIds = (bookingState: BookingState) => {
  if (bookingState.product?.productType !== "PASS") {
    return [];
  }

  const minChildAge =
    ProdAttrUtils.findDefaultBooka(bookingState.bookingAttributes)
      ?.value?.ageQuantities?.filter((x) => x.age.startsWith("CHILD"))
      ?.map((x) => ProdAttrUtils.CHILD.parseAge(x.age)?.ageFrom)
      ?.reduce((a, v) => Math.min(a, v), 100) ?? 100;
  // const minChildAge = -1;
  const childrenList = bookingState.childrenLs ?? [];
  const invalidAgeProductIds = childrenList
    .filter((product) => (product.product?.requiredAge ?? 0) > minChildAge)
    .map((product) => product.product?.id);

  return invalidAgeProductIds;
};

export const extractFreeForAgesProductIds = (
  bookingState: BookingState,
): number[] => {
  if (bookingState.product?.productType !== "PASS") {
    return [];
  }
  const ls = ProdAttrUtils.findDefaultBooka(bookingState.bookingAttributes)
    ?.value?.ageQuantities?.filter(
      (x) => x.age.startsWith("CHILD") && !!x.quantity,
    )
    ?.map((x) => ProdAttrUtils.CHILD.parseAge(x.age)?.ageFrom);
  if (!ls?.length) {
    return [];
  }
  const minChildAge = ls?.reduce((a, v) => Math.min(a, v), 100) ?? 100;

  const freeForAgesProductIds = bookingState.childrenLs
    ?.filter((child) => {
      const freeForAgesParsed = parseFreeForAges(child.product?.freeForAges);
      return freeForAgesParsed.includes(minChildAge);
    })
    .map((child) => child.product?.id)
    .filter((x) => !!x) as number[];

  //     const freeForAgeText = useMemo(() => {
  //   return !!product?.freeForAges &&
  //     ageLs.find((x) => parseFreeForAges(product?.freeForAges).includes(+x)) !==
  //       undefined
  //     ? (product.freeForAges ?? "").replaceAll(",", ", ").replaceAll("_", "-")
  //     : false;
  // }, [product?.freeForAges, ageLs]);

  return freeForAgesProductIds ?? [];
};

export const splitBookingBasedOnAge = (bookingState: BookingState) => {
  if (
    bookingState.childrenLs?.length === 1 ||
    bookingState.childrenLs?.length === 0
  ) {
    return [bookingState];
  }
  const ageQuantities = ProdAttrUtils.findDefaultBooka(
    bookingState.bookingAttributes,
  )?.value?.ageQuantities;
  try {
    if (!ageQuantities) {
      return [bookingState];
    }
    const ageProductLs = [] as {
      ages: string[];
      childrenLs: BookingState[];
      hashedChildrenLs: string;
    }[];
    for (const ageQuantity of ageQuantities) {
      if (!ageQuantity.quantity) continue;
      const childrenLs = deepCopy(bookingState?.childrenLs ?? []);
      if (ageQuantity.age === "ADULT") {
        ageProductLs.push({
          ages: [ageQuantity.age],
          childrenLs,
          hashedChildrenLs: childrenLs
            .map((x) => x.product?.id)
            .sort()
            .join(","),
        });

        continue;
      }
      const age = ProdAttrUtils.CHILD.parseAge(ageQuantity.age);
      if (age) {
        const ageNum = age.ageFrom;

        const childrenLsCopy = childrenLs.filter((s) => {
          // *BOOKMARK*: CHILD KHÔNG MUA SIM
          // if (s.product?.productType === "SIM") {
          //   // child không mua SIM
          //   console.log(s.product?.name + " child không mua SIM");
          //   return false;
          // }
          const freeForAges = parseFreeForAges(s.product?.freeForAges);
          if (freeForAges.includes(+ageNum)) {
            // nếu sản phẩm miễn phí cho độ tuổi này thì không được chọn
            console.log(
              s.product?.name + " sản phẩm miễn phí cho độ tuổi này",
              freeForAges,
            );
            return false;
          }

          if ((s.product?.requiredAge ?? 0) > ageNum) {
            // nếu sản phẩm yêu cầu độ tuổi lớn hơn thì không được chọn
            console.log(s.product?.name + " sản phẩm yêu cầu độ tuổi lớn hơn");
            return false;
          }
          return true;
        });

        const hashedChildrenLs = childrenLsCopy
          .map((x) => x.product?.id)
          .sort()
          .join(",");

        // find if same chidlrenLs
        // const found = ageProductLs.find((x) => {
        //   return x.hashedChildrenLs === hashedChildrenLs;
        // });
        // if (found) {
        //   found.ages.push(ageQuantity.age);
        // } else {

        // }
        ageProductLs.push({
          ages: [ageQuantity.age],
          childrenLs: childrenLsCopy,
          hashedChildrenLs,
        });
      }
    }

    // convert ageProductLs to BookingState[]
    const ret: BookingState[] = [];
    for (const ageProduct of ageProductLs) {
      if (!ageProduct?.childrenLs?.length) {
        continue;
      }
      const copied = deepCopy(bookingState);
      copied.childrenLs = ageProduct.childrenLs;

      const copiedAgeQuantities = ProdAttrUtils.findDefaultBooka(
        copied.bookingAttributes,
      )?.value?.ageQuantities?.filter((x) => ageProduct.ages.includes(x.age));

      if (!copiedAgeQuantities) {
        continue;
      }

      applyStateBasedOnAgeQuantity({
        copied,
        copiedAgeQuantities,
      });
      if (copied) {
        copied.bookingAttributes = modifyQuantity(
          ageProduct.ages[0],
          copied.bookingAttributes,
          copiedAgeQuantities,
        );
        copied.childrenLs?.forEach((child) => {
          child.bookingAttributes = modifyQuantity(
            ageProduct.ages[0],
            child.bookingAttributes,
            copiedAgeQuantities,
          );
        });
        ret.push(copied);
      }
    }
    return ret.sort((a, b) => {
      const aCount = a.childrenLs?.length ?? 0;
      const bCount = b.childrenLs?.length ?? 0;
      return bCount - aCount;
    });
  } catch (e) {
    console.error(e);
    return [bookingState];
  }
};

const modifyQuantity = (
  age: string,
  bookingAttributes: BookingAttributeDTO[],
  copiedAgeQuantities: AgeQuantityDTO[],
) => {
  if (age === "ADULT") {
    bookingAttributes.forEach((x) => {
      if (x.productAttribute.name === "CHILD") {
        x.value = undefined;
      }
    });
  } else {
    let foundedChild = false;
    bookingAttributes.forEach((x) => {
      if (x.productAttribute.name === "ADULT") {
        x.value = undefined;
      } else if (x.productAttribute.name === "CHILD") {
        const quantity =
          copiedAgeQuantities.find((kk) =>
            ProdAttrUtils.CHILD.includeAge(x.productAttribute.value, kk.age),
          )?.quantity ?? 0;
        x.value = BookVal.overwrite(x)({
          dpName: prodaNameUtils(x.productAttribute).join(" "),
          quantity,
        }).value;
        if (quantity) {
          foundedChild = true;
        }
      }
    });
    if (!foundedChild) {
      const quantity =
        copiedAgeQuantities.find((kk) => kk.age === age)?.quantity ?? 0;
      // fallback to adult
      console.log("fallback to adult", age, copiedAgeQuantities, quantity);
      bookingAttributes.forEach((x) => {
        if (x.productAttribute.name === "ADULT") {
          x.value = BookVal.overwrite(x)({
            dpName: prodaNameUtils(x.productAttribute).join(" "),
            quantity,
          }).value;
        }
      });
    }
  }
  return bookingAttributes;
};
export const shouldSelectTimeLater = (state: BookingState) => {
  return state?.bookingAttributes?.some((x) => x.value?.pickLater);
};

export const validateCalendar = (bookingAttributes: BookingAttributeDTO[]) => {
  const bookaCalendars = bookingAttributes.filter(
    ProdAttrUtils.CALENDAR.isBooka,
  );
  if (bookaCalendars.length) {
    if (
      bookaCalendars.some((x) => {
        if (x.value?.pickLater) return false;
        if (!x.value?.destDate) return true;
        const { pickTimeSlot } =
          PRODA_CALENDAR_CONF[x.productAttribute.name as ProdAttrNameCalendar];
        if (!pickTimeSlot) return false;
        return !x.value?.destTimeslot;
      })
    ) {
      return false;
    }
  }
  return true;
};
